import { componentQrl, inlinedQrl, _jsxBranch, useStore, useComputedQrl, useLexicalScope, useSignal, useVisibleTaskQrl, _jsxS, _jsxC, _IMMUTABLE, _fnSignal, _wrapProp } from "@builder.io/qwik";
import { Fragment } from "@builder.io/qwik/jsx-runtime";
import { Blocks } from "../../components/blocks/blocks.qwik.mjs";
import { InlinedScript } from "../../components/inlined-script.qwik.mjs";
import { InlinedStyles } from "../../components/inlined-styles.qwik.mjs";
import { TARGET } from "../../constants/target.qwik.mjs";
import { getClassPropName } from "../../functions/get-class-prop-name.qwik.mjs";
import { isEditing } from "../../functions/is-editing.qwik.mjs";
import { isPreviewing } from "../../functions/is-previewing.qwik.mjs";
import { getDefaultCanTrack } from "../../helpers/canTrack.qwik.mjs";
import { userAttributesService } from "../../helpers/user-attributes.qwik.mjs";
import { getPersonalizationScript, checkShouldRenderVariants, getUpdateVisibilityStylesScript, getBlocksToRender, DEFAULT_INDEX, SDKS_REQUIRING_RESET_APPROACH } from "./helpers.qwik.mjs";
import { filterWithCustomTargeting } from "./helpers/inlined-fns.qwik.mjs";
const PersonalizationContainer = /* @__PURE__ */ componentQrl(/* @__PURE__ */ inlinedQrl((props) => {
  var _a, _b, _c, _d, _e, _f, _g, _h;
  _jsxBranch();
  const state = useStore({
    scriptStr: getPersonalizationScript(props.variants, ((_a = props.builderBlock) == null ? void 0 : _a.id) || "none", (_c = (_b = props.builderContext) == null ? void 0 : _b.rootState) == null ? void 0 : _c.locale),
    shouldRenderVariants: checkShouldRenderVariants(props.variants, getDefaultCanTrack((_d = props.builderContext) == null ? void 0 : _d.canTrack)),
    shouldResetVariants: false,
    unsubscribers: [],
    updateVisibilityStylesScript: getUpdateVisibilityStylesScript(props.variants, ((_e = props.builderBlock) == null ? void 0 : _e.id) || "none", (_g = (_f = props.builderContext) == null ? void 0 : _f.rootState) == null ? void 0 : _g.locale),
    userAttributes: userAttributesService.getUserAttributes()
  });
  const attrs = useComputedQrl(/* @__PURE__ */ inlinedQrl(() => {
    const [props2] = useLexicalScope();
    return {
      ...props2.attributes,
      [getClassPropName()]: `builder-personalization-container ${props2.attributes[getClassPropName()] || ""}`
    };
  }, "PersonalizationContainer_component_attrs_useComputed_HvEf55mhkdc", [
    props
  ]));
  const filteredVariants = useComputedQrl(/* @__PURE__ */ inlinedQrl(() => {
    const [props2, state2] = useLexicalScope();
    return (props2.variants || []).filter((variant) => {
      var _a2, _b2, _c2, _d2;
      return filterWithCustomTargeting({
        ...((_b2 = (_a2 = props2.builderContext) == null ? void 0 : _a2.rootState) == null ? void 0 : _b2.locale) ? {
          locale: (_d2 = (_c2 = props2.builderContext) == null ? void 0 : _c2.rootState) == null ? void 0 : _d2.locale
        } : {},
        ...state2.userAttributes
      }, variant.query, variant.startDate, variant.endDate);
    });
  }, "PersonalizationContainer_component_filteredVariants_useComputed_0jlgEflkxcQ", [
    props,
    state
  ]));
  const blocksToRender = useComputedQrl(/* @__PURE__ */ inlinedQrl(() => {
    var _a2;
    const [filteredVariants2, props2, state2] = useLexicalScope();
    return getBlocksToRender({
      variants: props2.variants,
      fallbackBlocks: (_a2 = props2.builderBlock) == null ? void 0 : _a2.children,
      isHydrated: state2.shouldResetVariants,
      filteredVariants: filteredVariants2.value,
      previewingIndex: props2.previewingIndex
    });
  }, "PersonalizationContainer_component_blocksToRender_useComputed_aZOAYVB7Svg", [
    filteredVariants,
    props,
    state
  ]));
  const hideVariantsStyleString = useComputedQrl(/* @__PURE__ */ inlinedQrl(() => {
    const [props2] = useLexicalScope();
    return (props2.variants || []).map((_, index) => {
      var _a2;
      return `div[data-variant-id="${(_a2 = props2.builderBlock) == null ? void 0 : _a2.id}-${index}"] { display: none !important; } `;
    }).join("");
  }, "PersonalizationContainer_component_hideVariantsStyleString_useComputed_zcfmLIe0H1E", [
    props
  ]));
  const rootRef = useSignal();
  useVisibleTaskQrl(/* @__PURE__ */ inlinedQrl(() => {
    var _a2;
    const [filteredVariants2, props2, rootRef2, state2] = useLexicalScope();
    state2.shouldResetVariants = true;
    const unsub = userAttributesService.subscribeOnUserAttributesChange((attrs2) => {
      state2.userAttributes = attrs2;
    }, {
      fireImmediately: TARGET === "qwik"
    });
    if (!(isEditing() || isPreviewing())) {
      const variant = filteredVariants2.value[0];
      if (rootRef2.value) {
        rootRef2.value.dispatchEvent(new CustomEvent("builder.variantLoaded", {
          detail: {
            variant: variant || DEFAULT_INDEX,
            content: (_a2 = props2.builderContext) == null ? void 0 : _a2.content
          },
          bubbles: true
        }));
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            var _a3;
            if (entry.isIntersecting && rootRef2.value)
              rootRef2.value.dispatchEvent(new CustomEvent("builder.variantDisplayed", {
                detail: {
                  variant: variant || DEFAULT_INDEX,
                  content: (_a3 = props2.builderContext) == null ? void 0 : _a3.content
                },
                bubbles: true
              }));
          });
        });
        observer.observe(rootRef2.value);
      }
    }
    state2.unsubscribers.push(unsub);
  }, "PersonalizationContainer_component_useVisibleTask_Pr6C1pfMwDU", [
    filteredVariants,
    props,
    rootRef,
    state
  ]));
  return /* @__PURE__ */ _jsxS("div", {
    ref: rootRef,
    ...attrs.value,
    children: [
      state.shouldResetVariants && SDKS_REQUIRING_RESET_APPROACH.includes(TARGET) ? /* @__PURE__ */ _jsxC(Blocks, {
        get blocks() {
          return blocksToRender.value.blocks;
        },
        get parent() {
          var _a2;
          return (_a2 = props.builderBlock) == null ? void 0 : _a2.id;
        },
        get path() {
          return blocksToRender.value.path;
        },
        get context() {
          return props.builderContext;
        },
        get registeredComponents() {
          return props.builderComponents;
        },
        get BlocksWrapperProps() {
          var _a2, _b2;
          return {
            ...(_a2 = props.builderContext) == null ? void 0 : _a2.BlocksWrapperProps,
            "data-variant-id": `${(_b2 = props.builderBlock) == null ? void 0 : _b2.id}-${blocksToRender.value.index}`
          };
        },
        [_IMMUTABLE]: {
          BlocksWrapperProps: _fnSignal((p0, p1) => {
            var _a2, _b2;
            return {
              ...(_a2 = p1.builderContext) == null ? void 0 : _a2.BlocksWrapperProps,
              "data-variant-id": `${(_b2 = p1.builderBlock) == null ? void 0 : _b2.id}-${p0.value.index}`
            };
          }, [
            blocksToRender,
            props
          ], '{...p1.builderContext?.BlocksWrapperProps,"data-variant-id":`${p1.builderBlock?.id}-${p0.value.index}`}'),
          blocks: _fnSignal((p0) => p0.value.blocks, [
            blocksToRender
          ], "p0.value.blocks"),
          context: _fnSignal((p0) => p0.builderContext, [
            props
          ], "p0.builderContext"),
          parent: _fnSignal((p0) => {
            var _a2;
            return (_a2 = p0.builderBlock) == null ? void 0 : _a2.id;
          }, [
            props
          ], "p0.builderBlock?.id"),
          path: _fnSignal((p0) => p0.value.path, [
            blocksToRender
          ], "p0.value.path"),
          registeredComponents: _fnSignal((p0) => p0.builderComponents, [
            props
          ], "p0.builderComponents")
        }
      }, 3, "Xk_0") : null,
      !state.shouldResetVariants && SDKS_REQUIRING_RESET_APPROACH.includes(TARGET) || !SDKS_REQUIRING_RESET_APPROACH.includes(TARGET) ? /* @__PURE__ */ _jsxC(Fragment, {
        children: [
          state.shouldRenderVariants ? /* @__PURE__ */ _jsxC(Fragment, {
            children: [
              /* @__PURE__ */ _jsxC(InlinedStyles, {
                get nonce() {
                  var _a2;
                  return ((_a2 = props.builderContext) == null ? void 0 : _a2.nonce) || "";
                },
                get styles() {
                  return hideVariantsStyleString.value;
                },
                get id() {
                  var _a2;
                  return `variants-styles-${(_a2 = props.builderBlock) == null ? void 0 : _a2.id}`;
                },
                [_IMMUTABLE]: {
                  id: _fnSignal((p0) => {
                    var _a2;
                    return `variants-styles-${(_a2 = p0.builderBlock) == null ? void 0 : _a2.id}`;
                  }, [
                    props
                  ], "`variants-styles-${p0.builderBlock?.id}`"),
                  nonce: _fnSignal((p0) => {
                    var _a2;
                    return ((_a2 = p0.builderContext) == null ? void 0 : _a2.nonce) || "";
                  }, [
                    props
                  ], 'p0.builderContext?.nonce||""'),
                  styles: _fnSignal((p0) => p0.value, [
                    hideVariantsStyleString
                  ], "p0.value")
                }
              }, 3, "Xk_1"),
              /* @__PURE__ */ _jsxC(InlinedScript, {
                get nonce() {
                  var _a2;
                  return ((_a2 = props.builderContext) == null ? void 0 : _a2.nonce) || "";
                },
                get scriptStr() {
                  return state.updateVisibilityStylesScript;
                },
                get id() {
                  var _a2;
                  return `variants-visibility-script-${(_a2 = props.builderBlock) == null ? void 0 : _a2.id}`;
                },
                [_IMMUTABLE]: {
                  id: _fnSignal((p0) => {
                    var _a2;
                    return `variants-visibility-script-${(_a2 = p0.builderBlock) == null ? void 0 : _a2.id}`;
                  }, [
                    props
                  ], "`variants-visibility-script-${p0.builderBlock?.id}`"),
                  nonce: _fnSignal((p0) => {
                    var _a2;
                    return ((_a2 = p0.builderContext) == null ? void 0 : _a2.nonce) || "";
                  }, [
                    props
                  ], 'p0.builderContext?.nonce||""'),
                  scriptStr: _fnSignal((p0) => p0.updateVisibilityStylesScript, [
                    state
                  ], "p0.updateVisibilityStylesScript")
                }
              }, 3, "Xk_2"),
              (props.variants || []).map((variant, index) => {
                var _a2, _b2, _c2, _d2;
                return /* @__PURE__ */ _jsxC(Blocks, {
                  BlocksWrapperProps: {
                    ...(_a2 = props.builderContext) == null ? void 0 : _a2.BlocksWrapperProps,
                    "aria-hidden": true,
                    hidden: true,
                    "data-variant-id": `${(_b2 = props.builderBlock) == null ? void 0 : _b2.id}-${index}`
                  },
                  get blocks() {
                    return variant.blocks;
                  },
                  get parent() {
                    var _a3;
                    return (_a3 = props.builderBlock) == null ? void 0 : _a3.id;
                  },
                  path: `component.options.variants.${index}.blocks`,
                  get context() {
                    return props.builderContext;
                  },
                  get registeredComponents() {
                    return props.builderComponents;
                  },
                  children: /* @__PURE__ */ _jsxC(InlinedScript, {
                    get nonce() {
                      var _a3;
                      return ((_a3 = props.builderContext) == null ? void 0 : _a3.nonce) || "";
                    },
                    get scriptStr() {
                      return state.scriptStr;
                    },
                    id: `variants-script-${(_c2 = props.builderBlock) == null ? void 0 : _c2.id}-${index}`,
                    [_IMMUTABLE]: {
                      nonce: _fnSignal((p0) => {
                        var _a3;
                        return ((_a3 = p0.builderContext) == null ? void 0 : _a3.nonce) || "";
                      }, [
                        props
                      ], 'p0.builderContext?.nonce||""'),
                      scriptStr: _fnSignal((p0) => p0.scriptStr, [
                        state
                      ], "p0.scriptStr")
                    }
                  }, 3, "Xk_3"),
                  [_IMMUTABLE]: {
                    blocks: _wrapProp(variant, "blocks"),
                    context: _fnSignal((p0) => p0.builderContext, [
                      props
                    ], "p0.builderContext"),
                    parent: _fnSignal((p0) => {
                      var _a3;
                      return (_a3 = p0.builderBlock) == null ? void 0 : _a3.id;
                    }, [
                      props
                    ], "p0.builderBlock?.id"),
                    registeredComponents: _fnSignal((p0) => p0.builderComponents, [
                      props
                    ], "p0.builderComponents")
                  }
                }, 1, `${(_d2 = props.builderBlock) == null ? void 0 : _d2.id}-${index}`);
              })
            ]
          }, 1, "Xk_4") : null,
          /* @__PURE__ */ _jsxC(Blocks, {
            get blocks() {
              return blocksToRender.value.blocks;
            },
            get parent() {
              var _a2;
              return (_a2 = props.builderBlock) == null ? void 0 : _a2.id;
            },
            get path() {
              return blocksToRender.value.path;
            },
            get context() {
              return props.builderContext;
            },
            get registeredComponents() {
              return props.builderComponents;
            },
            get BlocksWrapperProps() {
              var _a2, _b2;
              return {
                ...(_a2 = props.builderContext) == null ? void 0 : _a2.BlocksWrapperProps,
                "data-variant-id": `${(_b2 = props.builderBlock) == null ? void 0 : _b2.id}-${blocksToRender.value.index}`
              };
            },
            children: state.shouldRenderVariants ? /* @__PURE__ */ _jsxC(InlinedScript, {
              get nonce() {
                var _a2;
                return ((_a2 = props.builderContext) == null ? void 0 : _a2.nonce) || "";
              },
              get scriptStr() {
                return state.scriptStr;
              },
              id: `variants-script-${(_h = props.builderBlock) == null ? void 0 : _h.id}-${DEFAULT_INDEX}`,
              [_IMMUTABLE]: {
                nonce: _fnSignal((p0) => {
                  var _a2;
                  return ((_a2 = p0.builderContext) == null ? void 0 : _a2.nonce) || "";
                }, [
                  props
                ], 'p0.builderContext?.nonce||""'),
                scriptStr: _fnSignal((p0) => p0.scriptStr, [
                  state
                ], "p0.scriptStr")
              }
            }, 3, "Xk_5") : null,
            [_IMMUTABLE]: {
              BlocksWrapperProps: _fnSignal((p0, p1) => {
                var _a2, _b2;
                return {
                  ...(_a2 = p1.builderContext) == null ? void 0 : _a2.BlocksWrapperProps,
                  "data-variant-id": `${(_b2 = p1.builderBlock) == null ? void 0 : _b2.id}-${p0.value.index}`
                };
              }, [
                blocksToRender,
                props
              ], '{...p1.builderContext?.BlocksWrapperProps,"data-variant-id":`${p1.builderBlock?.id}-${p0.value.index}`}'),
              blocks: _fnSignal((p0) => p0.value.blocks, [
                blocksToRender
              ], "p0.value.blocks"),
              context: _fnSignal((p0) => p0.builderContext, [
                props
              ], "p0.builderContext"),
              parent: _fnSignal((p0) => {
                var _a2;
                return (_a2 = p0.builderBlock) == null ? void 0 : _a2.id;
              }, [
                props
              ], "p0.builderBlock?.id"),
              path: _fnSignal((p0) => p0.value.path, [
                blocksToRender
              ], "p0.value.path"),
              registeredComponents: _fnSignal((p0) => p0.builderComponents, [
                props
              ], "p0.builderComponents")
            }
          }, 1, "Xk_6")
        ]
      }, 1, "Xk_7") : null
    ]
  }, null, 0, "Xk_8");
}, "PersonalizationContainer_component_kIsPnu9O2Ew"));
export {
  PersonalizationContainer,
  PersonalizationContainer as default
};
